/*
 * ProblemCanvas — Slice 7 Phase 2 problem scoping.
 *
 * Fires when the user clicks "Continue with me" on EnterL2Card. Sticky
 * pinned card showing the one problem we're scoping in depth (seeded
 * from L1.E via the scope.py helper). Each editable row maps to a
 * P-field on problem_card.data; workflows are workflow_subcard rows
 * hanging off the same problem.
 *
 * v1 surface:
 *   - Eyebrow + problem statement (P1, large, editable)
 *   - Departments pills (P2)
 *   - Success metric (P5, italic editorial accent)
 *   - Workflow list with name + steps preview
 *   - Add-workflow inline action
 *   - Footer CTAs: Continue with Orbit (close, let Inquirer run Phase 1)
 *     OR Draft PRD now (skip ahead if user feels done)
 *
 * Brand: compact 18-20px padding, black/blue/yellow triad, no card
 * border (surface color does the lift), Plex Serif italic for the
 * problem statement editorial moment.
 */

import React, { useState } from 'react';
import { ArrowRight, Plus, Pencil, Check } from 'lucide-react';

import type {
  ProblemCanvasPayload,
  ProblemCanvasWorkflow,
} from '../../lib/dossier-types';
import { addWorkflow, patchProblem, patchWorkflow, setProblemStatus } from '../../lib/intakeApi';


export interface ProblemCanvasProps {
  payload: ProblemCanvasPayload;
  /** User clicked "Continue with Orbit" — close the canvas and let
   *  the chat keep ranking Phase 1 questions. */
  onContinue?: () => void;
  /** User clicked "Draft PRD now" — skip deep scope. */
  onDraftPrdNow?: () => void;
  disabled?: boolean;
}


export const ProblemCanvas: React.FC<ProblemCanvasProps> = ({
  payload,
  onContinue,
  onDraftPrdNow,
  disabled,
}) => {
  const { problem } = payload;
  const [workflows, setWorkflows] = useState<ProblemCanvasWorkflow[]>(
    payload.workflows || [],
  );
  const [editingStatement, setEditingStatement] = useState(false);
  const [statementDraft, setStatementDraft] = useState<string>(
    (problem.data.statement as string | undefined) || '',
  );
  const [statementCommitted, setStatementCommitted] = useState<string>(
    (problem.data.statement as string | undefined) || '',
  );
  const [savingStatement, setSavingStatement] = useState(false);
  const [addingWorkflow, setAddingWorkflow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline workflow editor — the canvas's core verb: "scope this workflow."
  // Each workflow holds a trigger (when it fires), steps (what it does), and
  // tools (what it touches). Editing one of these is what turns a stub into a
  // scoped workflow — and once any workflow has all three, the problem flips
  // to SCOPED, which is what advances Phase 2 → Phase 3.
  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);
  const [wfTrigger, setWfTrigger] = useState('');
  const [wfSteps, setWfSteps] = useState('');
  const [wfTools, setWfTools] = useState('');
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  const openWorkflowEditor = (wf: ProblemCanvasWorkflow) => {
    setEditingWorkflowId(wf.id);
    setWfTrigger((wf.data.trigger as string | undefined) || '');
    setWfSteps((wf.data.steps as string | undefined) || '');
    setWfTools((wf.data.tools as string | undefined) || '');
    setError(null);
  };

  const cancelWorkflowEditor = () => {
    setEditingWorkflowId(null);
    setWfTrigger(''); setWfSteps(''); setWfTools('');
  };

  const saveWorkflow = async (wf: ProblemCanvasWorkflow) => {
    const trigger = wfTrigger.trim();
    const steps = wfSteps.trim();
    const tools = wfTools.trim();
    // Name the workflow from its trigger so the list reads meaningfully
    // instead of "New workflow".
    const name = trigger
      ? (trigger.length > 48 ? `${trigger.slice(0, 45).trim()}…` : trigger)
      : ((wf.data.name as string | undefined) || 'Workflow');
    const data = { name, trigger, steps, tools };
    setSavingWorkflow(true);
    setError(null);
    try {
      await patchWorkflow(wf.id, data);
      setWorkflows(prev => prev.map(w => (
        w.id === wf.id ? { ...w, data: { ...w.data, ...data } } : w
      )));
      // A workflow with trigger + steps + tools is "scoped enough" — flip the
      // problem to SCOPED so the phase machine advances Phase 2 → Phase 3.
      if (trigger && steps && tools) {
        try { await setProblemStatus(problem.id, 'SCOPED'); }
        catch (e) { console.error('[ProblemCanvas] setProblemStatus failed', e); }
      }
      setEditingWorkflowId(null);
      setWfTrigger(''); setWfSteps(''); setWfTools('');
    } catch (e) {
      console.error('[ProblemCanvas] saveWorkflow failed', e);
      setError("Couldn't save that workflow. Try again?");
    } finally {
      setSavingWorkflow(false);
    }
  };

  const departments = (problem.data.affected_departments as string[] | undefined) || [];
  const successMetric = (problem.data.success_metric as string | undefined) || '';

  const commitStatement = async () => {
    const next = statementDraft.trim();
    if (!next || next === statementCommitted) {
      setEditingStatement(false);
      return;
    }
    setSavingStatement(true);
    setError(null);
    try {
      await patchProblem(problem.id, { statement: next });
      setStatementCommitted(next);
      setEditingStatement(false);
    } catch (e) {
      console.error('[ProblemCanvas] patch statement failed', e);
      setError("Couldn't save that. Try again?");
    } finally {
      setSavingStatement(false);
    }
  };

  const handleAddWorkflow = async () => {
    setError(null);
    setAddingWorkflow(true);
    try {
      const wf = await addWorkflow(problem.id, { name: 'New workflow' });
      setWorkflows(prev => [
        ...prev,
        {
          id: wf.id,
          data: wf.data || { name: 'New workflow' },
          sort_order: wf.sort_order ?? prev.length,
        },
      ]);
      // Open the editor straight away — adding a workflow means scoping it,
      // not leaving an empty stub.
      setEditingWorkflowId(wf.id);
      setWfTrigger(''); setWfSteps(''); setWfTools('');
    } catch (e) {
      console.error('[ProblemCanvas] addWorkflow failed', e);
      setError("Couldn't add a workflow just now. Try again?");
    } finally {
      setAddingWorkflow(false);
    }
  };

  return (
    <div className="aa-problem-canvas">
      <header className="aa-problem-canvas-header">
        <span className="aa-problem-canvas-eyebrow">
          Scoping · {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
        </span>
        {editingStatement ? (
          <div className="aa-problem-canvas-statement-edit">
            <textarea
              className="aa-problem-canvas-statement-input"
              value={statementDraft}
              onChange={(e) => setStatementDraft(e.target.value)}
              rows={2}
              autoFocus
              disabled={savingStatement}
            />
            <div className="aa-problem-canvas-statement-actions">
              <button
                type="button"
                className="aa-problem-canvas-statement-save"
                onClick={commitStatement}
                disabled={savingStatement || !statementDraft.trim()}
              >
                <Check size={12} strokeWidth={2.5} />
                <span>Save</span>
              </button>
              <button
                type="button"
                className="aa-problem-canvas-statement-cancel"
                onClick={() => {
                  setStatementDraft(statementCommitted);
                  setEditingStatement(false);
                }}
                disabled={savingStatement}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="aa-problem-canvas-statement-display"
            onClick={() => setEditingStatement(true)}
            aria-label="Edit problem statement"
          >
            <h3 className="aa-problem-canvas-statement">
              {statementCommitted || 'Tell me what we\'re solving.'}
            </h3>
            <Pencil size={12} strokeWidth={2.25} className="aa-problem-canvas-statement-pencil" />
          </button>
        )}
      </header>

      {(departments.length > 0 || successMetric) && (
        <div className="aa-problem-canvas-meta">
          {departments.length > 0 && (
            <div className="aa-problem-canvas-meta-row">
              <span className="aa-problem-canvas-meta-label">Touches</span>
              <div className="aa-problem-canvas-dept-pills">
                {departments.map((d) => (
                  <span key={d} className="aa-problem-canvas-dept-pill">{d}</span>
                ))}
              </div>
            </div>
          )}
          {successMetric && (
            <div className="aa-problem-canvas-meta-row">
              <span className="aa-problem-canvas-meta-label">Win looks like</span>
              <p className="aa-problem-canvas-success">{successMetric}</p>
            </div>
          )}
        </div>
      )}

      <section className="aa-problem-canvas-workflows">
        <header className="aa-problem-canvas-workflows-header">
          <span className="aa-problem-canvas-workflows-eyebrow">Workflows</span>
          <button
            type="button"
            className="aa-problem-canvas-add-workflow"
            onClick={handleAddWorkflow}
            disabled={addingWorkflow || disabled}
          >
            <Plus size={12} strokeWidth={2.5} />
            <span>{addingWorkflow ? 'Adding…' : 'Add workflow'}</span>
          </button>
        </header>
        {workflows.length === 0 ? (
          <p className="aa-problem-canvas-workflows-empty">
            No workflows yet. Add one and we&apos;ll scope the trigger, steps, and tools.
          </p>
        ) : (
          <ul className="aa-problem-canvas-workflow-list" role="list">
            {workflows.map((wf, idx) => {
              const name = (wf.data.name as string | undefined) || `Workflow ${idx + 1}`;
              const steps = (wf.data.steps as string | undefined) || '';
              const trigger = (wf.data.trigger as string | undefined) || '';
              const tools = (wf.data.tools as string | undefined) || '';
              const preview = steps.length > 120 ? `${steps.slice(0, 117).trim()}…` : steps;
              const scoped = Boolean(trigger && steps && tools);
              const isEditing = editingWorkflowId === wf.id;

              if (isEditing) {
                return (
                  <li key={wf.id} className="aa-problem-canvas-workflow-row aa-problem-canvas-workflow-row--editing">
                    <span className="aa-problem-canvas-workflow-index">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="aa-problem-canvas-workflow-editor">
                      <label className="aa-problem-canvas-wf-label">Trigger — what starts it?</label>
                      <input
                        className="aa-problem-canvas-wf-input"
                        value={wfTrigger}
                        onChange={e => setWfTrigger(e.target.value)}
                        placeholder="A schedule, an incoming event, or a manual run."
                        autoFocus
                      />
                      <label className="aa-problem-canvas-wf-label">Steps — what it does, in order</label>
                      <textarea
                        className="aa-problem-canvas-wf-textarea"
                        value={wfSteps}
                        onChange={e => setWfSteps(e.target.value)}
                        rows={3}
                        placeholder="The main actions, start to finish."
                      />
                      <label className="aa-problem-canvas-wf-label">Tools — what it reads or writes</label>
                      <input
                        className="aa-problem-canvas-wf-input"
                        value={wfTools}
                        onChange={e => setWfTools(e.target.value)}
                        placeholder="The systems or apps it connects to."
                      />
                      <div className="aa-problem-canvas-wf-actions">
                        <button
                          type="button"
                          className="aa-problem-canvas-wf-save"
                          onClick={() => saveWorkflow(wf)}
                          disabled={savingWorkflow}
                        >
                          <Check size={13} strokeWidth={2.5} />
                          <span>{savingWorkflow ? 'Saving…' : 'Save workflow'}</span>
                        </button>
                        <button
                          type="button"
                          className="aa-problem-canvas-wf-cancel"
                          onClick={cancelWorkflowEditor}
                          disabled={savingWorkflow}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </li>
                );
              }

              return (
                <li
                  key={wf.id}
                  className="aa-problem-canvas-workflow-row"
                  onClick={() => openWorkflowEditor(wf)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="aa-problem-canvas-workflow-index">{String(idx + 1).padStart(2, '0')}</span>
                  <div className="aa-problem-canvas-workflow-body">
                    <span className="aa-problem-canvas-workflow-name">
                      {name}{scoped ? '' : ' · not scoped yet'}
                    </span>
                    {preview && (
                      <p className="aa-problem-canvas-workflow-steps">{preview}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="aa-problem-canvas-workflow-edit"
                    aria-label="Edit workflow"
                    onClick={(e) => { e.stopPropagation(); openWorkflowEditor(wf); }}
                  >
                    <Pencil size={13} strokeWidth={2.5} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {error && (
        <p className="aa-problem-canvas-error">{error}</p>
      )}

      <footer className="aa-problem-canvas-footer">
        <button
          type="button"
          className="aa-problem-canvas-primary"
          onClick={onContinue}
          disabled={disabled || !onContinue}
        >
          <span>Keep scoping with Orbit</span>
          <ArrowRight size={13} strokeWidth={2.5} />
        </button>
        {onDraftPrdNow && (
          <button
            type="button"
            className="aa-problem-canvas-secondary"
            onClick={onDraftPrdNow}
            disabled={disabled}
          >
            Draft PRD now
          </button>
        )}
      </footer>
    </div>
  );
};

export default ProblemCanvas;
